import {PayloadAction} from "@reduxjs/toolkit";
import {
  AirportApiGetAirportRequest,
  AirportApiGetAirportResponse,
  AirportEntity,
  BookingApiBrokerAircraftSearchRequest,
  BookingApiBrokerAircraftSearchResponse,
  BookingApiOrderRequest, BookingApiOrderResponse, CompanyEntity,
  OrderEntity, OrderRequestEntity,
  OrderRouteEntity, PlaneCategoryEntity,
  PageModel,
  BookingApiPlaneCategoryListRequest,
  BookingApiPlaneCategoryListResponse,
  PlaneCategoryEntity as PlaneCategoryEntityProto,
  FlightLegEntity,
} from "../../../proto/generated/api_entities_pb";
import {
  brokerAircraftSearch,
  multiLegRequest, multiLegRequestResult,
  oneWayRequest, oneWayRequestResult, roundTripRequest, roundTripRequestResult,
  setBrokerAircraftSearchResult
} from "../../reducers/newRequestReducer/newRequestReducer";
import {all, call, put, takeLatest} from "redux-saga/effects";
import {API} from "../../../API/API";
import {getErrorMessageByCode} from "../../../utils/constans/errorCodes/errorCodes";
import {getLocalDateAsISOString} from "../../../utils/helpers/dateHelpers/dateHelpers";
import {
  IBrokerAircraft,
  IBrokerAircraftSearchPayload,
  IMultiLegRequestPayload,
  IOneWayRequestPayload,
  IRoundTripRequestPayload, RequestTypeEnum
} from "../../reducers/newRequestReducer/newRequestReducerTypes";
import {metricsGoals} from "../../../utils/constans/metrics/goals";
import {Logger} from "../../../utils/logger/Logger";
import {InitialPage} from "../../../utils/constans/pagination/pagination";
import {PageModelLimits} from "../../../utils/constans/pagination/pageModelLimits";


function* oneWayRequestSaga({payload}: PayloadAction<IOneWayRequestPayload>) {
  const orderRoute: OrderRouteEntity = yield call(getOrderRouteEntity, {
    airportFromId: payload.airportFromId,
    airportToId: payload.airportToId,
    departureDate: payload.departureDate,
    pax: payload.pax,
    companionsAllowed: payload.companionsAllowed,
    planeCategoryIds: payload.planeCategoryIds,
    comment: payload.comment,
    companyIds: payload.companyIds,
  })

  const planeIdsCategoryEntity: PlaneCategoryEntity[] = payload.planeCategoryIds.map<PlaneCategoryEntity>(p => {
      return new PlaneCategoryEntity()
        .setPlanecategoryid(p)
    }
  )

  const orderRequests = payload.companyIds.map(id => new OrderRequestEntity().setFlycompany(new CompanyEntity().setCompanyid(id)));

  const req = new BookingApiOrderRequest()
    .setOrder(new OrderEntity()
      .setOrderroutesList([orderRoute])
      .setPlanecategoriesList(planeIdsCategoryEntity)
      .setCompanionsallowed(payload.companionsAllowed)
      .setType(RequestTypeEnum.ONE_WAY)
      .setComment(payload.comment)
      .setOrderrequestsList(orderRequests)
    )
    .setWelcomemessage(payload.comment);

  try {
    const res: BookingApiOrderResponse =
      yield call(API.orderCreate, req);

    if (res.getResultresponse()) {
      yield put(oneWayRequestResult({}));
      metricsGoals.orderCreated();
    } else {
      yield put(oneWayRequestResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage())
      }))
    }
  } catch {
  }
}


function* roundTripRequestSaga({payload}: PayloadAction<IRoundTripRequestPayload>) {
  const orderRouteThere: OrderRouteEntity = yield call(getOrderRouteEntity, {
    airportFromId: payload.airportFromId,
    airportToId: payload.airportToId,
    departureDate: payload.departureDateFrom,
    pax: payload.paxFrom,
    companionsAllowed: payload.companionsAllowed,
    planeCategoryIds: payload.planeCategoryIds,
    comment: payload.comment,
    companyIds: payload.companyIds,
  })

  //reverse airports because of round trip specifics
  const orderRouteBack: OrderRouteEntity = yield call(getOrderRouteEntity, {
    airportFromId: payload.airportToId,
    airportToId: payload.airportFromId,
    departureDate: payload.departureDateTo,
    pax: payload.paxTo,
    companionsAllowed: payload.companionsAllowed,
    planeCategoryIds: payload.planeCategoryIds,
    comment: payload.comment,
    companyIds: payload.companyIds,
  })

  const planeIdsCategoryEntity: PlaneCategoryEntity[] = payload.planeCategoryIds.map<PlaneCategoryEntity>(p => {
      return new PlaneCategoryEntity()
        .setPlanecategoryid(p)
    }
  )

  const orderRequests = payload.companyIds.map(id => new OrderRequestEntity().setFlycompany(new CompanyEntity().setCompanyid(id)));

  const req = new BookingApiOrderRequest()
    .setOrder(new OrderEntity()
      .setOrderroutesList([orderRouteThere, orderRouteBack])
      .setPlanecategoriesList(planeIdsCategoryEntity)
      .setCompanionsallowed(payload.companionsAllowed)
      .setType(RequestTypeEnum.ROUND_TRIP)
      .setComment(payload.comment)
      .setOrderrequestsList(orderRequests)
    )
    .setWelcomemessage(payload.comment);

  try {
    const res: BookingApiOrderResponse =
      yield call(API.orderCreate, req);

    if (res.getResultresponse()) {
      yield put(roundTripRequestResult({}));
      metricsGoals.orderCreated();
    } else {
      yield put(roundTripRequestResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage())
      }))
    }
  } catch {
  }
}


function* multiLegRequestSaga({payload}: PayloadAction<IMultiLegRequestPayload>) {
  const orderRoutes: OrderRouteEntity[] = yield all(payload.routes.map(route => call(getOrderRouteEntity, {
    airportFromId: route.airportFromId,
    airportToId: route.airportToId,
    departureDate: route.departureDate,
    pax: route.pax,
    companionsAllowed: payload.companionsAllowed,
    planeCategoryIds: payload.planeCategoryIds,
    comment: payload.comment,
    companyIds: payload.companyIds,
  })));

  const planeIdsCategoryEntity: PlaneCategoryEntity[] = payload.planeCategoryIds.map<PlaneCategoryEntity>(p => {
      return new PlaneCategoryEntity()
        .setPlanecategoryid(p)
    }
  )

  const orderRequests = payload.companyIds.map(id => new OrderRequestEntity().setFlycompany(new CompanyEntity().setCompanyid(id)));

  const req = new BookingApiOrderRequest()
    .setOrder(new OrderEntity()
      .setOrderroutesList(orderRoutes)
      .setPlanecategoriesList(planeIdsCategoryEntity)
      .setCompanionsallowed(payload.companionsAllowed)
      .setType(RequestTypeEnum.MULTI_LEG)
      .setComment(payload.comment)
      .setOrderrequestsList(orderRequests)
    )
    .setWelcomemessage(payload.comment);

  try {
    const res: BookingApiOrderResponse =
      yield call(API.orderCreate, req);

    if (res.getResultresponse()) {
      yield put(multiLegRequestResult({}));
      metricsGoals.orderCreated();
    } else {
      yield put(multiLegRequestResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage())
      }))
    }
  } catch {
  }
}


interface IGetOrderRouteEntity {
  airportFromId: number;
  airportToId: number;
  departureDate: Date;
  pax: number;
  planeCategoryIds: number[];
  companionsAllowed: boolean;
  comment: string;
  companyIds: number[];
}

function* getOrderRouteEntity(orderRoute: IGetOrderRouteEntity) {
  const airportFromResponse: AirportApiGetAirportResponse = yield call(
    API.getAirportById,
    new AirportApiGetAirportRequest().setAirportid(orderRoute.airportFromId)
  );

  const airportFrom = airportFromResponse.getAirport();
  if (!airportFrom)
    throw new Error("No airport with given id found");

  return new OrderRouteEntity()
    .setPax(orderRoute.pax)
    .setAirportfrom(airportFrom)
    .setAirportto(
      new AirportEntity().setAirportid(orderRoute.airportToId)
    )
    .setDeparturedate(getLocalDateAsISOString(orderRoute.departureDate))
}

function* brokerAircraftSearchSaga({payload}: PayloadAction<IBrokerAircraftSearchPayload>) {
  try {
    const req = new BookingApiBrokerAircraftSearchRequest()
      .setOriginairportid(payload.originAirportId)
      .setDestinationairportid(payload.destinationAirportId)
      .setPax(payload.pax);
    
    if (payload.paxTo !== undefined) {
      req.setPaxto(payload.paxTo);
    }
    
    // Handle multileg routes
    if (payload.multilegRoutes && payload.multilegRoutes.length > 0) {
      const multilegRoutesList = payload.multilegRoutes.map(leg => {
        const legEntity = new FlightLegEntity()
          .setAirportfromid(leg.airportFromId)
          .setAirporttoid(leg.airportToId)
          .setPax(leg.pax);
        
        if (leg.departureDate) {
          legEntity.setDeparturedate(leg.departureDate);
        }
        if (leg.departureTime) {
          legEntity.setDeparturetime(leg.departureTime);
        }
        
        return legEntity;
      });
      
      req.setMultilegroutesList(multilegRoutesList);
    }
    
    // Всегда устанавливаем дату и время, если они переданы
    if (payload.departureDate) {
      req.setDeparturedate(payload.departureDate);
      // Если время не указано, используем '00:00:00' по умолчанию
      const departureTime = payload.departureTime || '00:00:00';
      req.setDeparturetime(departureTime);
    }
    
    // Если категории не выбраны, получаем все категории
    let planeCategoryIds = payload.planeCategoryIds;
    if (!planeCategoryIds || planeCategoryIds.length === 0) {
      const categoryListReq = new BookingApiPlaneCategoryListRequest()
        .setPagemodel(
          new PageModel().setPage(InitialPage).setLimit(PageModelLimits.Select)
        );
      const categoryListResponse: BookingApiPlaneCategoryListResponse = yield call(API.getPlaneCategoryList, categoryListReq);
      planeCategoryIds = categoryListResponse.getPlanecategoriesList().map((pc: PlaneCategoryEntityProto) => pc.getPlanecategoryid());
    }
    
    if (planeCategoryIds && planeCategoryIds.length > 0) {
      req.setPlanecategoryidsList(planeCategoryIds);
    }
    if (payload.planeModel) {
      req.setPlanemodel(payload.planeModel);
    }
    if (payload.manufactureYearFrom) {
      req.setManufactureyearfrom(payload.manufactureYearFrom);
    }
    if (payload.manufactureYearTo) {
      req.setManufactureyearto(payload.manufactureYearTo);
    }
    if (payload.renovationYearFrom) {
      req.setRenovationyearfrom(payload.renovationYearFrom);
    }
    if (payload.renovationYearTo) {
      req.setRenovationyearto(payload.renovationYearTo);
    }
    if (payload.positioningDurationFromHours) {
      req.setPositioningdurationfromhours(payload.positioningDurationFromHours);
    }
    if (payload.positioningDurationToHours) {
      req.setPositioningdurationtohours(payload.positioningDurationToHours);
    }
    if (payload.landingCountFrom !== undefined) {
      req.setLandingcountfrom(payload.landingCountFrom);
    }
    if (payload.landingCountTo !== undefined) {
      req.setLandingcountto(payload.landingCountTo);
    }
    if (payload.refuelingCountFrom !== undefined) {
      req.setRefuelingcountfrom(payload.refuelingCountFrom);
    }
    if (payload.refuelingCountTo !== undefined) {
      req.setRefuelingcountto(payload.refuelingCountTo);
    }
    if (payload.updatedAtFrom) {
      req.setUpdatedatfrom(payload.updatedAtFrom);
    }
    if (payload.amenities && payload.amenities.length > 0) {
      req.setAmenitiesList(payload.amenities);
    }
    if (payload.operatorIds && payload.operatorIds.length > 0) {
      req.setOperatoridsList(payload.operatorIds);
    }
    if (payload.planeIds && payload.planeIds.length > 0) {
      req.setPlaneidsList(payload.planeIds);
    }

    const pageModel = new PageModel()
      .setPage(payload.page || 0)
      .setLimit(payload.limit || 50);
    req.setPagemodel(pageModel);

    const res: BookingApiBrokerAircraftSearchResponse = yield call(API.brokerAircraftSearch, req);

    Logger.collapsedLog('[brokerAircraftSearchSaga] Response received', {
      hasResultResponse: !!res.getResultresponse(),
      isSuccessful: res.getResultresponse()?.getIssuccessful(),
      aircraftCount: res.getAircraftList().length,
      totalCount: res.getTotalcount(),
      aircraftList: res.getAircraftList().map(a => ({
        planeId: a.getPlane()?.getPlaneid() ?? 0,
        registrationNumber: a.getPlane()?.getRegistrationnumber() ?? '',
        operatorName: '', // flyCompany не доступен в PlaneEntity
      })),
    }, 'aqua');

    // Если ResultResponse отсутствует или успешен, обрабатываем данные
    if (!res.getResultresponse() || res.getResultresponse()?.getIssuccessful()) {
      const aircraft: IBrokerAircraft[] = res.getAircraftList().map(aircraftEntity => {
        const plane = aircraftEntity.getPlane();
        const baseAirport = aircraftEntity.getBaseairport();
        const planeType = plane?.getPlanetype();
        const planeCategory = planeType?.getPlanecategory();
        
        // Парсим даты для получения года
        let manufactureYear: number | undefined;
        let renovationYear: number | undefined;
        if (plane?.getManufacturedate()) {
          const date = new Date(plane.getManufacturedate());
          if (!isNaN(date.getTime())) {
            manufactureYear = date.getFullYear();
          }
        }
        if (plane?.getRenovationdate()) {
          const date = new Date(plane.getRenovationdate());
          if (!isNaN(date.getTime())) {
            renovationYear = date.getFullYear();
          }
        }
        
        return {
          planeId: plane?.getPlaneid() ?? 0,
          registrationNumber: plane?.getRegistrationnumber() ?? '',
          flyCompanyId: 0, // flyCompany не доступен в PlaneEntity
          operatorName: '', // flyCompany не доступен в PlaneEntity
          planeTypeId: planeType?.getPlanetypeid() ?? 0,
          planeTypeName: planeType?.getName() ?? '',
          planeCategoryId: planeCategory?.getPlanecategoryid() ?? 0,
          planeCategoryName: planeCategory?.getName() ?? '',
          maxPassengers: plane?.getMaxpassengers() ?? 0,
          manufactureYear: manufactureYear ?? 0,
          renovationYear: renovationYear ?? 0,
          preliminaryPrice: aircraftEntity.getPreliminaryprice(),
          currencyId: aircraftEntity.hasCurrencyid() && aircraftEntity.getCurrencyid() > 0 
            ? aircraftEntity.getCurrencyid() 
            : (plane?.getCurrency()?.getCurrencyid() ?? 0), // Base currency (where price is calculated)
          currencyName: aircraftEntity.hasCurrencyid() && aircraftEntity.getCurrencyid() > 0
            ? undefined // Will be resolved from currency list
            : (plane?.getCurrency()?.getName() ?? undefined),
          planeCurrencyId: plane?.getCurrency()?.getCurrencyid() ?? undefined, // Plane's original currency
          planeCurrencyName: plane?.getCurrency()?.getName() ?? undefined,
          positioningDurationMinutes: aircraftEntity.getPositioningdurationminutes(),
          positioningDistanceKm: aircraftEntity.getPositioningdistancekm(),
          positioningTechnicalStopCount: aircraftEntity.getPositioningtechnicalstopcount(),
          positioningFromAirportId: aircraftEntity.getPositioningfromairportid() > 0 ? aircraftEntity.getPositioningfromairportid() : undefined,
          positioningDepartureDate: aircraftEntity.getPositioningdeparturedate() || undefined,
          flightDurationMinutes: aircraftEntity.getFlightdurationminutes(),
          lastLegFlightDurationMinutes: aircraftEntity.getLastlegflightdurationminutes() > 0 ? aircraftEntity.getLastlegflightdurationminutes() : undefined,
          flightDistanceKm: aircraftEntity.getFlightdistancekm(),
          totalDurationMinutes: aircraftEntity.getTotaldurationminutes(),
          technicalStopCount: aircraftEntity.getTechnicalstopcount(),
          refuelingCount: aircraftEntity.getRefuelingcount(),
          baseAirportId: baseAirport?.getAirportid() ?? 0,
          baseAirportIcao: baseAirport?.getIcaocode() ?? '',
          baseAirportIata: baseAirport?.getIatacode() ?? '',
          baseAirportName: baseAirport?.getName() ?? '',
          amenities: aircraftEntity.getAmenities() ? {
            airAmbulance: aircraftEntity.getAmenities()!.getAirambulance(),
            animalsAboard: aircraftEntity.getAmenities()!.getAnimalsaboard(),
            smokingAboard: aircraftEntity.getAmenities()!.getSmokingaboard(),
            airCargoFreight: aircraftEntity.getAmenities()!.getAircargofreight(),
            wifi: aircraftEntity.getAmenities()!.getWifi(),
            ovenAboard: aircraftEntity.getAmenities()!.getOvenaboard(),
          } : undefined,
          availableFromDate: aircraftEntity.getAvailablefromdate() || undefined,
          availableToDate: aircraftEntity.getAvailabletodate() || undefined,
          arrivalDate: aircraftEntity.getArrivaldate() || undefined,
          adjustedDepartureDateTime: aircraftEntity.getAdjusteddeparturedatetime() || undefined,
          routeErrorMessage: aircraftEntity.getRouteerrormessage() || undefined,
        };
      });

      // Извлекаем данные аэропортов из ответа
      const originAirport = res.getOriginairport();
      const destinationAirport = res.getDestinationairport();
      
      yield put(setBrokerAircraftSearchResult({
        aircraft,
        totalCount: res.getTotalcount(),
        aircraftEntities: res.getAircraftList(), // Сохраняем полные объекты
        originAirport: originAirport ? {
          code: originAirport.getIcaocode() || originAirport.getIatacode() || '',
          location_and_name: originAirport.getName() || '',
        } : undefined,
        destinationAirport: destinationAirport ? {
          code: destinationAirport.getIcaocode() || destinationAirport.getIatacode() || '',
          location_and_name: destinationAirport.getName() || '',
        } : undefined,
      }));
    } else {
      // Извлекаем данные аэропортов из ответа даже при ошибке
      const originAirport = res.getOriginairport();
      const destinationAirport = res.getDestinationairport();
      
      yield put(setBrokerAircraftSearchResult({
        aircraft: [],
        totalCount: 0,
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
        aircraftEntities: [],
        originAirport: originAirport ? {
          code: originAirport.getIcaocode() || originAirport.getIatacode() || '',
          location_and_name: originAirport.getName() || '',
        } : undefined,
        destinationAirport: destinationAirport ? {
          code: destinationAirport.getIcaocode() || destinationAirport.getIatacode() || '',
          location_and_name: destinationAirport.getName() || '',
        } : undefined,
      }));
    }
  } catch (error) {
    yield put(setBrokerAircraftSearchResult({
      aircraft: [],
      totalCount: 0,
      errorMessage: 'helpers.searchAircraftError',
      aircraftEntities: [],
    }));
  }
}

export function* newRequestSaga() {
  yield all([
    takeLatest(oneWayRequest.type, oneWayRequestSaga),
    takeLatest(roundTripRequest.type, roundTripRequestSaga),
    takeLatest(multiLegRequest.type, multiLegRequestSaga),
    takeLatest(brokerAircraftSearch.type, brokerAircraftSearchSaga),
  ])
}

